import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldOff } from "lucide-react";
import { api, formatApiError as formatErr } from "../lib/api";
import { PLACEMENT_INFO_SECTIONS } from "../data/placementInfoFields";

/* Carer-facing, read-only view of the family's shared "Getting Ready
   for Care" Placement record. Addressed by room_id for convenience;
   actual authorisation is an explicit family-granted share, resolved
   server-side via the room's placement_id — a Room alone never grants
   access.
   /care-discussion-room/:roomId/shared-info */

function ReadOnlyField({ field, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-soft">{field.label}</div>
      <p className="mt-1 text-sm text-ink whitespace-pre-wrap" data-testid={`shared-info-field-${field.key}`}>{value}</p>
    </div>
  );
}

function ReadOnlySection({ section, record }) {
  const anyValue = section.fields.some((f) => record[f.key]);
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid={`shared-info-section-${section.key}`}>
      <h2 className="font-display text-xl font-semibold">{section.label}</h2>
      {section.note && <p className="text-xs text-ink-soft mt-2 mb-4 max-w-2xl">{section.note}</p>}
      <div className="mt-4 grid sm:grid-cols-2 gap-5">
        {anyValue ? section.fields.map((f) => <ReadOnlyField key={f.key} field={f} value={record[f.key]} />)
          : <p className="text-sm text-ink-soft sm:col-span-2">Nothing recorded here yet.</p>}
      </div>
    </section>
  );
}

export default function SharedPlacementInfoView() {
  const { roomId } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    api.get(`/api/care-placement-info/shared/by-room/${roomId}`)
      .then((r) => setRecord(r.data))
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else setError(formatErr(err));
      })
      .finally(() => setLoading(false));
  }, [roomId]);

  if (loading) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="shared-info-loading"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…</div>;
  }

  if (forbidden) {
    return (
      <div className="container-jcc py-24 max-w-lg text-center" data-testid="shared-info-not-shared">
        <ShieldOff className="w-10 h-10 text-ink-soft mx-auto" />
        <h1 className="font-display text-2xl font-bold mt-4">Not shared with you yet</h1>
        <p className="mt-3 text-ink-muted">
          The family hasn&rsquo;t shared their &ldquo;Getting Ready for Care&rdquo; record with you for this Room.
          Ask them in your Room if you need this information.
        </p>
        <Link to={`/care-discussion-room/${roomId}`} className="btn-outline mt-6 inline-flex"><ArrowLeft className="w-4 h-4" /> Back to Room</Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-jcc py-24 max-w-lg text-center" data-testid="shared-info-error">
        <p className="text-ink-muted">{error}</p>
        <Link to={`/care-discussion-room/${roomId}`} className="btn-outline mt-6 inline-flex"><ArrowLeft className="w-4 h-4" /> Back to Room</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#fafaf7] min-h-screen" data-testid="page-shared-info">
      <div className="container-jcc py-10 md:py-14 max-w-4xl">
        <div className="mb-6">
          <Link to={`/care-discussion-room/${roomId}`} className="btn-outline !px-4 !py-2 text-sm" data-testid="shared-info-back-to-room">
            <ArrowLeft className="w-4 h-4" /> Back to Care Partnership Room
          </Link>
        </div>
        <div className="mb-8">
          <div className="overline text-purple mb-3">Family Care Office</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">Getting Ready for Care</h1>
          <p className="mt-3 text-ink-muted leading-relaxed max-w-2xl">
            Read-only. Shared with you by the family — you cannot edit this record.
          </p>
          {record.updated_at && (
            <p className="mt-3 text-sm text-ink-soft" data-testid="shared-info-last-updated">
              Last updated {new Date(record.updated_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </div>
        <div className="space-y-6">
          {PLACEMENT_INFO_SECTIONS.map((s) => <ReadOnlySection key={s.key} section={s} record={record} />)}
        </div>
      </div>
    </div>
  );
}
